import { parseEntryDate } from './finances';

// Reporting periods for the Accounting page.
//
// A period is a plain inclusive { start, end } pair of *local* dates (see
// parseEntryDate in lib/finances.js for why local matters), plus a label for
// statement headings. Either bound may be null, meaning "unbounded" — that's
// how "All time" is represented rather than inventing sentinel dates.

export const PERIOD_PRESETS = [
  'This month',
  'Last month',
  'This quarter',
  'Last quarter',
  'Year to date',
  'Last year',
  'All time',
];

export const CUSTOM_PERIOD = 'Custom range';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);

function formatDay(date) {
  return `${date.getDate()} ${MONTHS[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;
}

// Resolves a selection into concrete bounds. `value` is either a preset name,
// a four-digit year string (the per-year options in the dropdown), or
// CUSTOM_PERIOD.
export function resolvePeriod(value, { customStart, customEnd, today = new Date() } = {}) {
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (value) {
    case 'This month':
      return {
        start: startOfMonth(now),
        end: endOfMonth(now),
        label: `${MONTHS[now.getMonth()]} ${now.getFullYear()}`,
      };
    case 'Last month': {
      const ref = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        start: startOfMonth(ref),
        end: endOfMonth(ref),
        label: `${MONTHS[ref.getMonth()]} ${ref.getFullYear()}`,
      };
    }
    case 'This quarter':
      return {
        start: startOfQuarter(now),
        end: endOfQuarter(now),
        label: `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`,
      };
    case 'Last quarter': {
      const ref = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
      return {
        start: startOfQuarter(ref),
        end: endOfQuarter(ref),
        label: `Q${Math.floor(ref.getMonth() / 3) + 1} ${ref.getFullYear()}`,
      };
    }
    case 'Year to date':
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: now,
        label: `1 Jan – ${formatDay(now)}`,
      };
    case 'Last year': {
      const y = now.getFullYear() - 1;
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31), label: String(y) };
    }
    case 'All time':
      return { start: null, end: null, label: 'All time' };
    case CUSTOM_PERIOD: {
      const start = customStart ? parseEntryDate(customStart) : null;
      const end = customEnd ? parseEntryDate(customEnd) : null;
      let label = 'Custom range';
      if (start && end) label = `${formatDay(start)} – ${formatDay(end)}`;
      else if (start) label = `From ${formatDay(start)}`;
      else if (end) label = `Up to ${formatDay(end)}`;
      return { start, end, label };
    }
    default: {
      // A bare year from the dropdown.
      const y = Number(value);
      if (Number.isFinite(y) && y > 1900) {
        return { start: new Date(y, 0, 1), end: new Date(y, 11, 31), label: String(y) };
      }
      return { start: null, end: null, label: 'All time' };
    }
  }
}

export function filterByPeriod(entries, period, dateKey = 'entry_date') {
  if (!period || (!period.start && !period.end)) return entries;
  return entries.filter((entry) => {
    const d = parseEntryDate(entry[dateKey]);
    if (!d) return false;
    if (period.start && d < period.start) return false;
    if (period.end && d > period.end) return false;
    return true;
  });
}

// Concrete bounds for the things that can't work with an open-ended period
// (the month-by-month table, the balance sheet's "as at" date). Falls back to
// the span of the data itself, then to the current year.
export function effectiveRange(period, entries = [], dateKey = 'entry_date') {
  const dates = entries
    .map((e) => parseEntryDate(e[dateKey]))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const today = new Date();

  const start =
    period?.start || dates[0] || new Date(today.getFullYear(), 0, 1);
  const end =
    period?.end || dates[dates.length - 1] || new Date(today.getFullYear(), 11, 31);

  return { start, end };
}

// Month buckets spanned by a range, so the monthly table generalises from a
// full year (12 rows) to a quarter (3) without special-casing either.
export function monthsBetween(start, end) {
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    months.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      label: `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

export function toDateInputValue(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}
